/** Fraction digits the indicator prints, and so the precision its direction has to agree with. */
export const CHANGE_DECIMALS = 1;

// Past this the figure stops describing anything: it only happens when the
// earlier period sits next to zero, and the quotient explodes. A rate measured
// against an income next to zero explodes the same way, so points share the cap.
export const CHANGE_CAP = 999;

export type ChangeUnit = 'percent' | 'points';
export type ChangeDirection = 'up' | 'down' | 'flat';

/**
 * The change as displayed, rounded half away from zero. `toFixed` and Intl
 * round the nearest double instead, which sits just under an exact half: 0.35
 * arrives as 0.34999 and would print as 0,3 where the measured value is 0,4.
 * A resulting -0 collapses to 0, so a small negative never prints as "-0,0%".
 */
export const roundChange = (change: number) => {
  const factor = 10 ** CHANGE_DECIMALS;
  const rounded = (Math.sign(change) * Math.round(Math.abs(change) * factor)) / factor;
  return rounded === 0 ? 0 : rounded;
};

/** Direction of the value as displayed, not the raw one: 0.04 rounds to a flat "0,0%". */
export const changeDirection = (change: number): ChangeDirection => {
  const shown = roundChange(change);
  if (shown > 0) return 'up';
  if (shown < 0) return 'down';
  return 'flat';
};

export const isCappedChange = (change: number) => Math.abs(roundChange(change)) > CHANGE_CAP;

interface ChangeFormat {
  locale: string;
  formatPercent: (value: number, fractionDigits?: number) => string;
}

/** The unsigned size of a change, worded the way the indicator prints it, for the sentence that explains it. */
export const changeAmount = (
  change: number,
  unit: ChangeUnit,
  { locale, formatPercent }: ChangeFormat,
) => {
  const capped = isCappedChange(change);
  const size = capped ? CHANGE_CAP : Math.abs(roundChange(change));
  const digits = capped ? 0 : CHANGE_DECIMALS;
  const amount =
    unit === 'points'
      ? size.toLocaleString(locale, {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        })
      : formatPercent(size, digits);
  return capped ? `>${amount}` : amount;
};
